import { useAppSelector } from '@store/hooks'
import { useSettingsSubpageLoading } from '@pages/Settings/SettingsSubpageGate.component'
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useSettingsHeader } from '@pages/Settings/SettingsHeader.context'
import { areValuesEqual } from '@utils/settings.util'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import {
  fetchClinicOpeningHoursApi,
  saveClinicOpeningHoursApi,
} from '@services/api/clinic-opening-hours.api'
import {
  fetchClinicOverridesApi,
  saveClinicOverridesApi,
} from '@services/api/clinic-overrides.api'
import {
  fetchClinicHolidaysApi,
  saveClinicHolidaysApi,
} from '@services/api/clinic-holidays.api'
import {
  IClinicOpeningHours,
  ISaveClinicOpeningHoursPayload,
} from '@dataTypes/clinic-opening-hours.type'
import { IClinicOverrideRow } from '@dataTypes/clinic-overrides.type'
import { IClinicHolidayRow } from '@dataTypes/clinic-holidays.type'
import { amPmLabelToHms, formatHmsAsAmPm } from '@utils/phoneManagement.utils'
import {
  AddHolidayFormData,
  AddOverrideFormData,
  holidayFormToApiRow,
  overrideFormToApiRow,
  createDefaultWeekSchedule,
  DateOverrideRow,
  DaySchedule,
  holidayApiToGridRow,
  HolidayRow,
  OPENING_HOURS_DAYS,
  OpeningHoursDayKey,
  overrideApiToGridRow,
} from './openingHours.data'

const LOADING_CONTAINER = 'opening-hours-settings'

export interface IOpeningHoursForm {
  sameWorkingHours: boolean
  firstDayOfWeek: OpeningHoursDayKey
  weekSchedule: Record<OpeningHoursDayKey, DaySchedule>
}

const EMPTY_FORM: IOpeningHoursForm = {
  sameWorkingHours: false,
  firstDayOfWeek: 'Sunday',
  weekSchedule: createDefaultWeekSchedule(),
}

const formValuesForCompare = (
  form: IOpeningHoursForm,
): (string | boolean)[] => {
  const dayValues = OPENING_HOURS_DAYS.flatMap((day) => {
    const row = form.weekSchedule[day]
    return [row.enabled, row.startTime, row.endTime]
  })
  return [form.sameWorkingHours, form.firstDayOfWeek, ...dayValues]
}

const apiToForm = (data: IClinicOpeningHours): IOpeningHoursForm => {
  const weekSchedule = createDefaultWeekSchedule()
  OPENING_HOURS_DAYS.forEach((day) => {
    const row = data.schedule.find((s) => s.day_of_week === day)
    if (row) {
      weekSchedule[day] = {
        enabled: row.is_open,
        startTime: formatHmsAsAmPm(row.start_time),
        endTime: formatHmsAsAmPm(row.end_time),
      }
    }
  })

  const firstDay = OPENING_HOURS_DAYS.includes(
    data.first_day_of_week as OpeningHoursDayKey,
  )
    ? (data.first_day_of_week as OpeningHoursDayKey)
    : 'Sunday'

  return {
    sameWorkingHours: data.same_opening_hours,
    firstDayOfWeek: firstDay,
    weekSchedule,
  }
}

const formToApiPayload = (
  form: IOpeningHoursForm,
): ISaveClinicOpeningHoursPayload => ({
  first_day_of_week: form.firstDayOfWeek,
  same_opening_hours: form.sameWorkingHours,
  schedule: OPENING_HOURS_DAYS.map((day) => {
    const row = form.weekSchedule[day]
    return {
      day_of_week: day,
      is_open: row.enabled,
      start_time: amPmLabelToHms(row.startTime),
      end_time: amPmLabelToHms(row.endTime),
    }
  }),
})

export interface IOpeningHoursHooks {
  sameWorkingHours: boolean
  setSameWorkingHours: (value: boolean) => void
  firstDayOfWeek: OpeningHoursDayKey
  setFirstDayOfWeek: (value: OpeningHoursDayKey) => void
  weekSchedule: Record<OpeningHoursDayKey, DaySchedule>
  updateDay: (day: OpeningHoursDayKey, patch: Partial<DaySchedule>) => void
  handleReset: () => void
  handleSave: () => Promise<void>
  hasChanges: boolean
  loading: boolean
  dataReady: boolean
  setHeaderButtons: Dispatch<SetStateAction<ReactNode>>
  clinicId: string
  dateOverrides: DateOverrideRow[]
  holidays: HolidayRow[]
  overridesSource: IClinicOverrideRow[]
  holidaysSource: IClinicHolidayRow[]
  handleSaveOverride: (
    form: AddOverrideFormData,
    editId?: string,
  ) => Promise<void>
  handleDeleteOverride: (id: string) => Promise<void>
  handleSaveHoliday: (
    form: AddHolidayFormData,
    editId?: string,
  ) => Promise<void>
  handleDeleteHoliday: (id: string) => Promise<void>
  savingOverride: boolean
  savingHoliday: boolean
}

const useOpeningHours = (): IOpeningHoursHooks => {
  const { setHeaderButtons } = useSettingsHeader()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const { showToast } = useNotificationToast()
  const selectedClinic = useAppSelector((s) => s.clinic.selectedClinic)
  const clinicId = selectedClinic?.clinic_id ?? ''

  const [savedBaseline, setSavedBaseline] =
    useState<IOpeningHoursForm>(EMPTY_FORM)
  const [form, setForm] = useState<IOpeningHoursForm>(EMPTY_FORM)
  const [loading, setLoading] = useState(false)
  const [dataReady, setDataReady] = useState(false)
  const [overrides, setOverrides] = useState<IClinicOverrideRow[]>([])
  const [holidays, setHolidays] = useState<IClinicHolidayRow[]>([])
  const [savingOverride, setSavingOverride] = useState(false)
  const [savingHoliday, setSavingHoliday] = useState(false)

  useSettingsSubpageLoading(!dataReady)

  const dateOverrides = useMemo(
    () => overrides.map(overrideApiToGridRow),
    [overrides],
  )

  const holidayRows = useMemo(
    () => holidays.map(holidayApiToGridRow),
    [holidays],
  )

  const hasChanges = useMemo(
    () =>
      !areValuesEqual(
        formValuesForCompare(form),
        formValuesForCompare(savedBaseline),
      ),
    [form, savedBaseline],
  )

  useEffect(() => {
    if (!hasChanges) return
    const onBeforeUnload = (e: BeforeUnloadEvent): void => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [hasChanges])

  const loadOpeningHours = useCallback(async (): Promise<void> => {
    if (!clinicId) {
      setSavedBaseline(EMPTY_FORM)
      setForm(EMPTY_FORM)
      setOverrides([])
      setHolidays([])
      setDataReady(true)
      return
    }

    setLoading(true)
    try {
      const [hoursRes, overridesRes, holidaysRes] = await Promise.all([
        fetchClinicOpeningHoursApi(clinicId),
        fetchClinicOverridesApi(clinicId),
        fetchClinicHolidaysApi(clinicId),
      ])

      if (hoursRes.statusCode !== 200 || !hoursRes.data?.opening_hours) {
        showToast(
          createFailedToast(
            'Opening hours',
            hoursRes.message || 'Failed to load opening hours',
          ),
        )
      } else {
        const mapped = apiToForm(hoursRes.data.opening_hours)
        setSavedBaseline(mapped)
        setForm(mapped)
      }

      if (
        overridesRes.statusCode === 200 &&
        overridesRes.data?.clinic_overrides
      ) {
        setOverrides(overridesRes.data.clinic_overrides.overrides ?? [])
      } else {
        setOverrides([])
        if (overridesRes.statusCode !== 200) {
          showToast(
            createFailedToast(
              'Date override',
              overridesRes.message || 'Failed to load date overrides',
            ),
          )
        }
      }

      if (holidaysRes.statusCode === 200 && holidaysRes.data?.clinic_holidays) {
        setHolidays(holidaysRes.data.clinic_holidays.holidays ?? [])
      } else {
        setHolidays([])
        if (holidaysRes.statusCode !== 200) {
          showToast(
            createFailedToast(
              'Holidays',
              holidaysRes.message || 'Failed to load holidays',
            ),
          )
        }
      }
    } catch {
      showToast(
        createFailedToast('Opening hours', 'Failed to load opening hours'),
      )
    } finally {
      setLoading(false)
      setDataReady(true)
    }
  }, [clinicId, showToast])

  useEffect(() => {
    void loadOpeningHours()
  }, [loadOpeningHours])

  const updateDay = useCallback(
    (day: OpeningHoursDayKey, patch: Partial<DaySchedule>): void => {
      setForm((prev) => {
        const nextSchedule = {
          ...prev.weekSchedule,
          [day]: { ...prev.weekSchedule[day], ...patch },
        }
        const same = prev.sameWorkingHours
        if (same && patch.startTime !== undefined) {
          OPENING_HOURS_DAYS.forEach((d) => {
            if (d !== day && nextSchedule[d].enabled) {
              nextSchedule[d] = {
                ...nextSchedule[d],
                startTime: patch.startTime!,
              }
            }
          })
        }
        if (same && patch.endTime !== undefined) {
          OPENING_HOURS_DAYS.forEach((d) => {
            if (d !== day && nextSchedule[d].enabled) {
              nextSchedule[d] = { ...nextSchedule[d], endTime: patch.endTime! }
            }
          })
        }
        if (same && patch.enabled !== undefined && day !== 'Sunday') {
          OPENING_HOURS_DAYS.forEach((d) => {
            if (d !== 'Sunday' && d !== day) {
              nextSchedule[d] = {
                ...nextSchedule[d],
                enabled: patch.enabled!,
              }
            }
          })
        }
        return { ...prev, weekSchedule: nextSchedule }
      })
    },
    [],
  )

  const handleReset = useCallback((): void => {
    setForm({
      ...savedBaseline,
      weekSchedule: { ...savedBaseline.weekSchedule },
    })
  }, [savedBaseline])

  const handleSave = useCallback(async (): Promise<void> => {
    if (!clinicId) return
    setLoading(true)
    showLoading({ container: LOADING_CONTAINER })
    try {
      const res = await saveClinicOpeningHoursApi(
        clinicId,
        formToApiPayload(form),
      )
      if (res.statusCode !== 200 || !res.data?.opening_hours) {
        showToast(
          createFailedToast(
            'Opening hours',
            res.message || 'Failed to save opening hours',
          ),
        )
        return
      }
      const mapped = apiToForm(res.data.opening_hours)
      setSavedBaseline(mapped)
      setForm(mapped)
      showToast(
        createSuccessToast('Saved', 'Opening hours saved successfully.'),
      )
    } catch (e) {
      const message =
        e instanceof Error ? e.message : 'Failed to save opening hours'
      showToast(createFailedToast('Opening hours', message))
    } finally {
      setLoading(false)
      hideLoading(LOADING_CONTAINER)
    }
  }, [clinicId, form, hideLoading, showLoading, showToast])

  const persistOverrides = useCallback(
    async (
      nextOverrides: IClinicOverrideRow[],
      successMessage: string,
    ): Promise<void> => {
      if (!clinicId) return
      setSavingOverride(true)
      showLoading({ container: LOADING_CONTAINER })
      try {
        const res = await saveClinicOverridesApi(clinicId, {
          overrides: nextOverrides,
        })
        if (res.statusCode !== 200 || !res.data?.clinic_overrides) {
          showToast(
            createFailedToast(
              'Date override',
              res.message || 'Failed to save date overrides',
            ),
          )
          throw new Error(res.message || 'Failed to save date overrides')
        }
        setOverrides(res.data.clinic_overrides.overrides ?? [])
        showToast(createSuccessToast('Saved', successMessage))
      } catch (e) {
        const message =
          e instanceof Error ? e.message : 'Failed to save date overrides'
        showToast(createFailedToast('Date override', message))
        throw e
      } finally {
        setSavingOverride(false)
        hideLoading(LOADING_CONTAINER)
      }
    },
    [clinicId, hideLoading, showLoading, showToast],
  )

  const persistHolidays = useCallback(
    async (
      nextHolidays: IClinicHolidayRow[],
      successMessage: string,
    ): Promise<void> => {
      if (!clinicId) return
      setSavingHoliday(true)
      showLoading({ container: LOADING_CONTAINER })
      try {
        const res = await saveClinicHolidaysApi(clinicId, {
          holidays: nextHolidays,
        })
        if (res.statusCode !== 200 || !res.data?.clinic_holidays) {
          showToast(
            createFailedToast(
              'Holiday',
              res.message || 'Failed to save holidays',
            ),
          )
          throw new Error(res.message || 'Failed to save holidays')
        }
        setHolidays(res.data.clinic_holidays.holidays ?? [])
        showToast(createSuccessToast('Saved', successMessage))
      } catch (e) {
        const message =
          e instanceof Error ? e.message : 'Failed to save holidays'
        showToast(createFailedToast('Holiday', message))
        throw e
      } finally {
        setSavingHoliday(false)
        hideLoading(LOADING_CONTAINER)
      }
    },
    [clinicId, hideLoading, showLoading, showToast],
  )

  const handleSaveOverride = useCallback(
    async (formData: AddOverrideFormData, editId?: string): Promise<void> => {
      const nextOverrides = editId
        ? overrides.map((row) =>
            row.clinic_override_id === editId
              ? overrideFormToApiRow(formData, editId)
              : row,
          )
        : [...overrides, overrideFormToApiRow(formData)]
      await persistOverrides(
        nextOverrides,
        editId
          ? 'Date override updated successfully.'
          : 'Date override added successfully.',
      )
    },
    [overrides, persistOverrides],
  )

  const handleDeleteOverride = useCallback(
    async (id: string): Promise<void> => {
      const nextOverrides = overrides.filter(
        (row) => row.clinic_override_id !== id,
      )
      await persistOverrides(
        nextOverrides,
        'Date override deleted successfully.',
      )
    },
    [overrides, persistOverrides],
  )

  const handleSaveHoliday = useCallback(
    async (formData: AddHolidayFormData, editId?: string): Promise<void> => {
      const nextHolidays = editId
        ? holidays.map((row) =>
            row.clinic_holiday_id === editId
              ? holidayFormToApiRow(formData, editId)
              : row,
          )
        : [...holidays, holidayFormToApiRow(formData)]
      await persistHolidays(
        nextHolidays,
        editId
          ? 'Holiday updated successfully.'
          : 'Holiday added successfully.',
      )
    },
    [holidays, persistHolidays],
  )

  const handleDeleteHoliday = useCallback(
    async (id: string): Promise<void> => {
      const nextHolidays = holidays.filter(
        (row) => row.clinic_holiday_id !== id,
      )
      await persistHolidays(nextHolidays, 'Holiday deleted successfully.')
    },
    [holidays, persistHolidays],
  )

  return {
    sameWorkingHours: form.sameWorkingHours,
    setSameWorkingHours: (value) =>
      setForm((prev) => ({ ...prev, sameWorkingHours: value })),
    firstDayOfWeek: form.firstDayOfWeek,
    setFirstDayOfWeek: (value) =>
      setForm((prev) => ({ ...prev, firstDayOfWeek: value })),
    weekSchedule: form.weekSchedule,
    updateDay,
    handleReset,
    handleSave,
    hasChanges,
    loading,
    dataReady,
    setHeaderButtons,
    clinicId,
    dateOverrides,
    holidays: holidayRows,
    overridesSource: overrides,
    holidaysSource: holidays,
    handleSaveOverride,
    handleDeleteOverride,
    handleSaveHoliday,
    handleDeleteHoliday,
    savingOverride,
    savingHoliday,
  }
}

export default useOpeningHours
